import React, { ReactElement } from 'react';

import { DialogContent, DialogOverlay } from '@reach/dialog';
import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';
import CloseButton from 'components/close-button';
import usePreventBodyScroll from 'hooks/use-prevent-body-scroll';
import filterProps from 'utils/filter-props';

import * as animate from './animations';

interface IDrawerProps {
  allowsDismiss?: boolean;
  onDismiss: OnClick;
  children: ReactElement;
  isVisible?: boolean;
  disableScroll?: boolean;
  // rest property
  [key: string]: any;
}

const DrawerContainer = styled(filterProps(DialogOverlay, 'isVisible', 'disableScroll'))<
  IDrawerProps
>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 100vw;
  ${Styles.mobile`
  animation: ${(p: IDrawerProps) => (p.isVisible ? animate.down : animate.up)} 0.35s forwards ease;
  `}
  ${Styles.desktop`
    > div {
      animation: ${(p: IDrawerProps) =>
        p.isVisible ? animate.left : animate.right} 0.35s forwards ease;
    }
  `}
  animation: ${(p: IDrawerProps) =>
    p.isVisible ? animate.fadeIn : animate.fadeOut} 0.35s forwards;
  z-index: ${Styles.zIndex.overlay + 100};
  overflow-y: ${(p: IDrawerProps) => (p.disableScroll ? 'hidden' : 'auto')};
  &:focus {
    outline: none;
  }
  ${Styles.mobileFullscreen`
    top: env(safe-area-inset-top);
    height: calc(100vh - env(safe-area-inset-top));
  `}
`;

const DrawerContent = styled(DialogContent)`
  display: flex;
  position: absolute;
  flex-direction: column;
  top: 0;
  right: 0;
  min-height: 100vh;
  width: 100%;
  height: auto;
  background: ${Styles.color.primary};
  box-shadow: -4px 0 4px 0 rgba(0, 0, 0, 0.1);
  color: ${Styles.color.white};
  /* webkit hack to ensure content renders with -webkit-overflow-scrolling: touch above */
  > * {
    -webkit-transform: translateZ(0px);
  }

  ${Styles.desktop`
    width: auto;
  `}

  outline: 0;
`;

const MobileOptimizedDrawerContent = styled(
  filterProps(DrawerContent, 'isVisible', 'disableScroll')
)`
  position: fixed;
  // Pixel family phones in Landscape Mode still needed
  @media only screen and (min-height: 410px) and (max-height: 420px) and (min-width: 800px) {
    height: 80%;
    overflow-y: ${(p: IDrawerProps) => (p.disableScroll ? 'hidden' : 'auto')};
  }
`;

const StyledCloseButton = styled(CloseButton)`
  align-self: flex-start;
  background-color: transparent;
  color: ${Styles.color.white};
  position: absolute;
  left: 0.3rem;
  top: 0.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${Styles.zIndex.top};
  svg {
    height: 3rem;
  }
`;

const Drawer = ({
  allowsDismiss = true,
  className,
  children,
  onDismiss,
  isVisible,
  disableScroll,
  ...props
}: IDrawerProps) => {
  usePreventBodyScroll();
  return (
    <DrawerContainer isVisible={isVisible} disableScroll={disableScroll}>
      <MobileOptimizedDrawerContent
        className={className}
        role="dialog"
        aria-labelledby="drawer-heading"
        disableScroll={disableScroll}
        {...props}
      >
        {allowsDismiss && <StyledCloseButton onClick={onDismiss} />}
        {children}
      </MobileOptimizedDrawerContent>
    </DrawerContainer>
  );
};

export default Drawer;
